import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import type { ResultOf } from 'gql.tada'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Button } from '@/components'
import { Select } from '@/components_deprecated'
import { useModuleConfigs } from '@/modules/companies/CompanySettings/hooks'
import {
  COMPANY_USER_WORK_INFRINGEMENT_SETTINGS_QUERY,
  PATCH_COMPANY_USER_WORK_INFRINGEMENT_SETTINGS,
} from '@/modules/work-infringements/fats-settings-operations'
import { ProfileCard } from './components'

type QueryResult = ResultOf<
  typeof COMPANY_USER_WORK_INFRINGEMENT_SETTINGS_QUERY
>
type Override = QueryResult['companyUserWorkInfringementSettings']

const INHERIT = ''

// String-valued form so an empty value cleanly means "inherit the company default".
interface OverrideForm {
  referencePeriodWeeks: string
  maxDailyWorkHours: string
  nightRuleEnabled: string
  nightDailyLimitHours: string
  maxWeeklyWorkHours: string
  maxAverageWeeklyWorkHours: string
  weeklyOrdinaryHours: string
  annualOvertimeLimitHours: string
}

const numToField = (value: number | null | undefined) =>
  value == null ? INHERIT : String(value)
const boolToField = (value: boolean | null | undefined) =>
  value == null ? INHERIT : String(value)
const fieldToNum = (value: string) => (value === INHERIT ? null : Number(value))
const fieldToBool = (value: string) =>
  value === INHERIT ? null : value === 'true'

export const FATSUserOverride: React.FC<{ userId: string }> = ({ userId }) => {
  const { active } = useModuleConfigs()
  const { data, loading } = useQuery(
    COMPANY_USER_WORK_INFRINGEMENT_SETTINGS_QUERY,
    { variables: { userId }, skip: !userId || !active.workInfringements }
  )
  if (!active.workInfringements || loading || !data) return null
  return (
    <FATSUserOverrideForm
      userId={userId}
      override={data.companyUserWorkInfringementSettings}
    />
  )
}

const FATSUserOverrideForm: React.FC<{
  userId: string
  override: Override
}> = ({ userId, override }) => {
  const t = useTranslate({
    title: 'settings.fats-parameters.user-override-title',
    inherit: 'settings.fats-parameters.inherit-company',
    periodLength: 'settings.fats.period-length',
    maxDailyWorkLimit: 'settings.fats.max-daily-work-limit',
    nightRule: 'settings.fats-parameters.night-rule',
    nightDailyLimit: 'settings.fats-parameters.night-daily-limit',
    maxWeekly: 'settings.fats-parameters.max-weekly',
    avgWeekly: 'settings.fats-parameters.avg-weekly',
    weeklyOrdinary: 'settings.fats-parameters.weekly-ordinary',
    annualOvertime: 'settings.fats-parameters.annual-overtime',
    nWeeks: ['settings.fats.n-weeks', { n: 0 }],
    nHours: ['common.n-hours', { n: 0 }],
    enabled: 'common.enabled',
    disabled: 'common.disabled',
    save: 'common.save',
  })

  const [form, setForm] = useState<OverrideForm>({
    referencePeriodWeeks: numToField(override?.referencePeriodWeeks),
    maxDailyWorkHours: numToField(override?.maxDailyWorkHours),
    nightRuleEnabled: boolToField(override?.nightRuleEnabled),
    nightDailyLimitHours: numToField(override?.nightDailyLimitHours),
    maxWeeklyWorkHours: numToField(override?.maxWeeklyWorkHours),
    maxAverageWeeklyWorkHours: numToField(override?.maxAverageWeeklyWorkHours),
    weeklyOrdinaryHours: numToField(override?.weeklyOrdinaryHours),
    annualOvertimeLimitHours: numToField(override?.annualOvertimeLimitHours),
  })

  const [patchOverride, { loading: saving }] = useMutation(
    PATCH_COMPANY_USER_WORK_INFRINGEMENT_SETTINGS
  )

  const set = (field: keyof OverrideForm) => (value: string | null) =>
    setForm(prev => ({ ...prev, [field]: value ?? INHERIT }))

  const inherit = { label: t.inherit, value: INHERIT }
  const weekOptions = [
    inherit,
    ...Array.from({ length: 11 }, (_, i) => ({
      label: t.nWeeks({ n: 16 + i }),
      value: String(16 + i),
    })),
  ]
  const hourOptions = (from: number, count: number) => [
    inherit,
    ...Array.from({ length: count }, (_, i) => ({
      label: t.nHours({ n: from + i }),
      value: String(from + i),
    })),
  ]
  const annualOvertimeOptions = [
    inherit,
    ...[200, 250, 300, 350, 400].map(n => ({
      label: t.nHours({ n }),
      value: String(n),
    })),
  ]
  const nightRuleOptions = [
    inherit,
    { label: t.enabled, value: 'true' },
    { label: t.disabled, value: 'false' },
  ]

  async function handleSave() {
    // Night window is a company-wide definition, so it is not overridden per driver.
    await patchOverride({
      variables: {
        userId,
        referencePeriodWeeks: fieldToNum(form.referencePeriodWeeks),
        maxDailyWorkHours: fieldToNum(form.maxDailyWorkHours),
        nightRuleEnabled: fieldToBool(form.nightRuleEnabled),
        nightDailyLimitHours: fieldToNum(form.nightDailyLimitHours),
        maxWeeklyWorkHours: fieldToNum(form.maxWeeklyWorkHours),
        maxAverageWeeklyWorkHours: fieldToNum(form.maxAverageWeeklyWorkHours),
        weeklyOrdinaryHours: fieldToNum(form.weeklyOrdinaryHours),
        annualOvertimeLimitHours: fieldToNum(form.annualOvertimeLimitHours),
      },
    })
  }

  return (
    <ProfileCard title={t.title}>
      <div className="flex flex-col gap-4">
        <FieldRow label={t.periodLength}>
          <Select
            options={weekOptions}
            value={form.referencePeriodWeeks}
            onChange={set('referencePeriodWeeks')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        <FieldRow label={t.maxDailyWorkLimit}>
          <Select
            options={hourOptions(13, 4)}
            value={form.maxDailyWorkHours}
            onChange={set('maxDailyWorkHours')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        <FieldRow label={t.maxWeekly}>
          <Select
            options={hourOptions(48, 19)}
            value={form.maxWeeklyWorkHours}
            onChange={set('maxWeeklyWorkHours')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        <FieldRow label={t.avgWeekly}>
          <Select
            options={hourOptions(40, 15)}
            value={form.maxAverageWeeklyWorkHours}
            onChange={set('maxAverageWeeklyWorkHours')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        <FieldRow label={t.weeklyOrdinary}>
          <Select
            options={hourOptions(35, 14)}
            value={form.weeklyOrdinaryHours}
            onChange={set('weeklyOrdinaryHours')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        <FieldRow label={t.annualOvertime}>
          <Select
            options={annualOvertimeOptions}
            value={form.annualOvertimeLimitHours}
            onChange={set('annualOvertimeLimitHours')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        <FieldRow label={t.nightRule}>
          <Select
            options={nightRuleOptions}
            value={form.nightRuleEnabled}
            onChange={set('nightRuleEnabled')}
            nullable={false}
            width="220px"
          />
        </FieldRow>
        {form.nightRuleEnabled !== 'false' && (
          <FieldRow label={t.nightDailyLimit}>
            <Select
              options={hourOptions(8, 6)}
              value={form.nightDailyLimitHours}
              onChange={set('nightDailyLimitHours')}
              nullable={false}
              width="220px"
            />
          </FieldRow>
        )}
        <div>
          <Button onClick={handleSave} disabled={saving}>
            {t.save}
          </Button>
        </div>
      </div>
    </ProfileCard>
  )
}

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-center justify-between gap-4">
    <label className="text-sm">{label}</label>
    {children}
  </div>
)
